import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotify } from "ra-core";
import qrcode from "qrcode-generator";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { getApiBaseUrl } from "@/lib/api-client";

/**
 * WhatsApp pelo número do próprio usuário (migration 285, api-server
 * whatsapp/whatsapp.mjs): o briefing diário da North IA chega na conversa
 * "Você" do WhatsApp dele. Conectar = QR lido no celular (Aparelhos
 * conectados › Conectar um aparelho) ou, como alternativa, código de 8 letras
 * para o número digitado (Conectar com número de telefone). O QR chega em
 * whatsapp_conexoes.codigo como "qr:<texto>" e troca a cada ~20 s.
 */

export type LinhaWhatsapp = {
  estado: "pareando" | "conectado" | "desconectado" | "erro";
  numero: string | null;
  codigo: string | null;
  erro: string | null;
};

const celularBonito = (n: string | null) => {
  const d = (n ?? "").replace(/\D/g, "").replace(/^55/, "");
  return d.length === 11 ? `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}` : d;
};

/** Estado e texto do cartão a partir da linha de whatsapp_conexoes. */
export const resumoWhatsapp = (w: LinhaWhatsapp | null | undefined) => {
  if (w?.estado === "conectado") {
    return { estado: "conectado" as const, detalhe: `${celularBonito(w.numero)} · briefing diário no WhatsApp` };
  }
  if (w?.estado === "pareando") return { estado: "incompleto" as const, detalhe: "Aguardando a conexão no celular." };
  if (w?.estado === "erro") return { estado: "nao_conectado" as const, detalhe: w.erro ?? "Não foi possível conectar." };
  return { estado: "nao_conectado" as const, detalhe: "Briefing diário no WhatsApp do próprio número." };
};

/** A linha de whatsapp_conexoes do usuário (RLS: a própria; super admin vê todas). */
export const useConexaoWhatsapp = (crmUserId: number | string | null | undefined) => {
  const [linha, setLinha] = useState<LinhaWhatsapp | null>(null);
  const recarregar = useCallback(async () => {
    if (crmUserId == null) return null;
    const { data } = await supabase
      .from("whatsapp_conexoes" as never)
      .select("estado, numero, codigo, erro")
      .eq("crm_user_id", Number(crmUserId))
      .maybeSingle();
    const l = (data ?? null) as LinhaWhatsapp | null;
    setLinha(l);
    return l;
  }, [crmUserId]);
  useEffect(() => {
    void recarregar();
  }, [recarregar]);
  return { linha, recarregar };
};

const PREFIXO_QR = "qr:";

/**
 * Para reduzir o risco de bloqueio do número (conexão não oficial). O aceite
 * vai ao servidor com a versão (data da redação) e fica gravado na conexão
 * (migration 296). Mudou o texto: mudar a versão.
 */
export const VERSAO_REGRAS = "2026-10-29";
const REGRAS_DE_USO = [
  "Conexão não oficial, pelo protocolo do WhatsApp Web. O WhatsApp pode restringir ou bloquear o número.",
  "Uso restrito ao briefing da North IA, na conversa do próprio número.",
  "Número com histórico de uso; número recém-criado tem mais risco de bloqueio.",
  "Sem disparo em massa e sem mensagem para quem não salvou o número.",
  "Celular com o WhatsApp em uso e conectado à internet.",
  "Sem outra automação no mesmo número.",
];

const RegrasDeUso = ({ ciente, aoMudar }: { ciente: boolean; aoMudar: (v: boolean) => void }) => (
  <div className="space-y-2 rounded-md border bg-muted/40 p-3">
    <p className="text-sm font-medium">Regras de uso</p>
    <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
      {REGRAS_DE_USO.map((r) => <li key={r}>{r}</li>)}
    </ul>
    <label className="flex items-center gap-2 pt-1 text-sm">
      <Checkbox checked={ciente} onCheckedChange={(v) => aoMudar(v === true)} />
      Ciente das regras e do risco de bloqueio do número.
    </label>
  </div>
);

/** QR do pareamento, como imagem (sem HTML injetado). */
const QrWhatsapp = ({ texto }: { texto: string }) => {
  const src = useMemo(() => {
    const q = qrcode(0, "L");
    q.addData(texto);
    q.make();
    return q.createDataURL(5, 2);
  }, [texto]);
  return <img src={src} alt="QR de conexão do WhatsApp" className="mx-auto h-60 w-60 rounded bg-white p-1 [image-rendering:pixelated]" />;
};

/** Diálogo de conexão: QR (padrão) ou número → código de 8 letras → conectado. */
export const DialogoWhatsapp = ({
  aberto,
  aoFechar,
  recarregar,
}: {
  aberto: boolean;
  aoFechar: () => void;
  recarregar: () => Promise<LinhaWhatsapp | null>;
}) => {
  const notify = useNotify();
  const [numero, setNumero] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [codigo, setCodigo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aguardando, setAguardando] = useState(false);
  const [porNumero, setPorNumero] = useState(false);
  const [ciente, setCiente] = useState(false);

  useEffect(() => {
    if (!aberto) {
      setCodigo(null);
      setErro(null);
      setAguardando(false);
      setPorNumero(false);
      setCiente(false);
    }
  }, [aberto]);

  // Enquanto pareia: a Lambda grava o código e depois "conectado" na linha.
  useEffect(() => {
    if (!aguardando) return;
    const inicio = Date.now();
    const timer = window.setInterval(async () => {
      const l = await recarregar();
      if (l?.codigo) setCodigo(l.codigo);
      if (l?.estado === "conectado") {
        window.clearInterval(timer);
        setAguardando(false);
        notify("WhatsApp conectado.");
        aoFechar();
      } else if (l?.estado === "erro" || Date.now() - inicio > 240_000) {
        window.clearInterval(timer);
        setAguardando(false);
        setErro(l?.erro ?? "Não foi possível conectar.");
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [aguardando, recarregar, notify, aoFechar]);

  const conectar = async (modo: "qr" | "codigo") => {
    setEnviando(true);
    setErro(null);
    setCodigo(null);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const r = await fetch(`${getApiBaseUrl()}/whatsapp/parear`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ modo, aceite_regras: VERSAO_REGRAS, ...(modo === "codigo" ? { numero } : {}) }),
      });
      if (!r.ok) {
        const corpo = await r.json().catch(() => ({}));
        throw new Error(corpo.message || "Não foi possível iniciar a conexão.");
      }
      setAguardando(true);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(o) => !o && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Conexão WhatsApp</DialogTitle>
          <DialogDescription>Briefing diário da North IA na conversa do próprio número.</DialogDescription>
        </DialogHeader>
        {codigo?.startsWith(PREFIXO_QR) ? (
          <div className="space-y-3">
            <QrWhatsapp texto={codigo.slice(PREFIXO_QR.length)} />
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>WhatsApp no celular › Configurações › Aparelhos conectados.</li>
              <li>Conectar um aparelho.</li>
              <li>Apontar a câmera para o QR acima.</li>
            </ol>
          </div>
        ) : codigo ? (
          <div className="space-y-3">
            <p className="text-sm">Código de conexão:</p>
            <p className="text-center font-mono text-3xl font-bold tracking-widest">{codigo}</p>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>WhatsApp no celular › Configurações › Aparelhos conectados.</li>
              <li>Conectar um aparelho › Conectar com número de telefone.</li>
              <li>Digitar o código acima.</li>
            </ol>
          </div>
        ) : porNumero ? (
          <div className="space-y-1.5">
            <Label htmlFor="whatsapp-numero">Celular com WhatsApp</Label>
            <Input
              id="whatsapp-numero"
              inputMode="tel"
              placeholder="(41) 99999-9999"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              disabled={aguardando}
            />
            {aguardando && <p className="text-xs text-muted-foreground">Gerando o código…</p>}
          </div>
        ) : (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Conexão pelo QR, lido no celular em Aparelhos conectados.</p>
            {aguardando && <p className="text-xs">Gerando o QR…</p>}
            {!aguardando && (
              <button type="button" className="text-xs underline underline-offset-2 hover:text-foreground" onClick={() => setPorNumero(true)}>
                Conectar com número de telefone
              </button>
            )}
          </div>
        )}
        {!codigo && <RegrasDeUso ciente={ciente} aoMudar={setCiente} />}
        {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar}>
            {codigo ? "Fechar" : "Cancelar"}
          </Button>
          {!codigo && porNumero && (
            <Button type="button" onClick={() => void conectar("codigo")} disabled={!ciente || enviando || aguardando || numero.replace(/\D/g, "").length < 10}>
              {enviando ? "Enviando..." : "Gerar código"}
            </Button>
          )}
          {!codigo && !porNumero && (
            <Button type="button" onClick={() => void conectar("qr")} disabled={!ciente || enviando || aguardando}>
              {enviando ? "Enviando..." : "Gerar QR"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Mensagem de teste para a conversa "Você" (api-server /whatsapp/teste) e
 * espera o registro em whatsapp_envios, até ~90 s. Erro = mensagem pronta.
 */
export const enviarTesteWhatsapp = async (crmUserId: number | string) => {
  // Folga de 1 min contra relógio adiantado do computador.
  const desde = new Date(Date.now() - 60_000).toISOString();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const r = await fetch(`${getApiBaseUrl()}/whatsapp/teste`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: "{}",
  });
  if (!r.ok) throw new Error("Não foi possível enviar o teste.");
  for (let i = 0; i < 30; i++) {
    await new Promise((ok) => setTimeout(ok, 3000));
    const { data: envio } = await supabase
      .from("whatsapp_envios" as never)
      .select("status, erro")
      .eq("crm_user_id", Number(crmUserId))
      .eq("origem", "teste")
      .gte("criado_em", desde)
      .order("criado_em", { ascending: false })
      .limit(1)
      .maybeSingle();
    const e = envio as { status: string; erro: string | null } | null;
    if (e?.status === "enviado") return;
    if (e?.status === "erro") throw new Error(`Não foi possível enviar o teste (${e.erro ?? "erro"}).`);
  }
  throw new Error("Não foi possível confirmar o envio do teste.");
};

export const desconectarWhatsapp = async () => {
  const { error } = await supabase.rpc("whatsapp_desconectar" as never);
  if (error) throw new Error(error.message);
};
